import { failure, isFailure, isOk, ok, pending, Result } from "@/common/common";
import { RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import {
  DCEvent,
  EventDispatcher,
  httpGet,
  httpPost,
  HttpResponse,
  showError,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { Language } from "@/i18n/languages";
import { FileUploadConfig, filterFileUploadConfig } from "@/fileUpload/fileUploadDomain";
import {
  AccountCompanyOption,
  AddFieldFormModel,
  commandError,
  CompanyCustomField,
  CustomFieldDataType,
  customFieldDataTypes,
  CustomFieldDefinition,
  CustomFieldEventEntry,
  customFieldEventLabel,
  CustomFieldObjectType,
  CustomFieldsImportResult,
  CustomFieldSubjectItem,
  CustomFieldValue,
  dataTypeIcons,
  dataTypeLabels,
  definitionAppliesTo,
  EntityValueFormModel,
  fetchContactCandidates,
  setValueCommand,
  StoredValueEntry,
  subjectDisplayName,
  toCompanyCustomField,
} from "./customFieldsDomain";
import { openEntityDataImport } from "./entityDataImportDomain";
import { openCompanyRoleImport } from "./companyRoleImportDomain";
import { openOwnershipImport } from "@/ownershipImport/ownershipImportDomain";
import { openFieldHistoryImport } from "./fieldHistoryImportDomain";

export type AccountFieldsTab = "entity" | "board-and-ceo";

export const tabObjectType: Record<AccountFieldsTab, CustomFieldObjectType> = {
  entity: "COMPANY",
  "board-and-ceo": "COMPANY_ROLE",
};

const definitionTab = (definition: CustomFieldDefinition): AccountFieldsTab =>
  definition.objectType === "COMPANY_ROLE" ? "board-and-ceo" : "entity";

export type AccountFieldsTypeFilter = CustomFieldDataType | "all";

export type SubjectFieldValue = { orgNo: string; subject: string } & StoredValueEntry;

export type FieldValueRow = CustomFieldSubjectItem & StoredValueEntry;

export type DeleteFieldRow = { subject: string; label: string } & StoredValueEntry;

export type FieldPanelTab = "details" | "values" | "log";

export type FieldValueFilter = "all" | "set" | "not-set";

export type PanelValueEdit = {
  row: FieldValueRow;
  model: EntityValueFormModel;
};

export type PanelValueHistory = {
  subjectName: string;
  field: CompanyCustomField;
  result: Result<CustomFieldEventEntry[]>;
};

export type FieldPanelState = {
  definitionId: string;
  tab: FieldPanelTab;
  valuesResult: Result<SubjectFieldValue[]>;
  logResult: Result<CustomFieldEventEntry[]>;
  valueSearchModel: StringFieldModel;
  valueFilter: RadioFieldModel<FieldValueFilter>;
  editValue?: PanelValueEdit;
  history?: PanelValueHistory;
};

export type DeleteFieldState = {
  definition: CustomFieldDefinition;
  valuesResult: Result<SubjectFieldValue[]>;
  isDeleting: boolean;
  error?: TMessage;
};

export type AccountFieldsState = {
  accountId: string;
  accountName: string;
  companies: AccountCompanyOption[];
  tab: AccountFieldsTab;
  definitionsResult: Result<CustomFieldDefinition[]>;
  subjects: Record<CustomFieldObjectType, Result<CustomFieldSubjectItem[]>>;
  searchModel: StringFieldModel;
  typeFilter: RadioFieldModel<AccountFieldsTypeFilter>;
  addFieldModel?: AddFieldFormModel;
  deleteField?: DeleteFieldState;
  fieldPanel?: FieldPanelState;
  isImporting: boolean;
  importResult?: CustomFieldsImportResult;
  uploadConfig?: FileUploadConfig;
};

export const initAccountFields = (
  accountId: string,
  accountName: string,
  companies: AccountCompanyOption[],
  editDefinitionId?: string,
): DCEvent[] => [
  {
    type: "update-store",
    data: { accountFields: defaultAccountFieldsState(accountId, accountName, companies) },
  },
  fetchDefinitions(accountId, editDefinitionId),
  fetchUploadConfig(accountId),
  fetchSubjects(accountId, "COMPANY"),
  fetchSubjects(accountId, "COMPANY_ROLE"),
];

const fetchSubjects = (accountId: string, objectType: CustomFieldObjectType): DCEvent =>
  httpGet({
    url: `/enterprise-accounts/${accountId}/custom-fields/subjects?objectType=${objectType}`,
    onSuccess: (res: HttpResponse<CustomFieldSubjectItem[]>) => [
      updateSubjects(objectType, ok(res.data)),
    ],
    onFailure: () => [updateSubjects(objectType, failure())],
  });

const updateSubjects = (
  objectType: CustomFieldObjectType,
  result: Result<CustomFieldSubjectItem[]>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    accountFields: (accountFields) =>
      accountFields && {
        ...accountFields,
        subjects: { ...accountFields.subjects, [objectType]: result },
      },
  },
});

export const subjectsOf = (
  state: AccountFieldsState,
  objectType: CustomFieldObjectType,
): CustomFieldSubjectItem[] => {
  const result = state.subjects[objectType];
  return isOk(result) ? result.data : [];
};

const fetchUploadConfig = (accountId: string): DCEvent =>
  httpGet({
    url: `/enterprise-accounts/${accountId}/custom-fields/file-upload-config`,
    onSuccess: (res: HttpResponse<FileUploadConfig>) => [
      updateAccountFields({ uploadConfig: filterFileUploadConfig(res.data, "xlsx") }),
    ],
    onFailure: () => [],
  });

export const defaultAccountFieldsState = (
  accountId: string,
  accountName: string,
  companies: AccountCompanyOption[],
): AccountFieldsState => ({
  accountId,
  accountName,
  companies,
  tab: "entity",
  definitionsResult: pending(),
  subjects: { COMPANY: pending(), COMPANY_ROLE: pending() },
  isImporting: false,
  searchModel: new StringFieldModel(),
  typeFilter: new RadioFieldModel<AccountFieldsTypeFilter>({
    value: "all",
    options: ["all", ...customFieldDataTypes],
    idFn: (type) => type,
    labelFn: (type) => (type === "all" ? { key: "entity-data.all-types" } : dataTypeLabels[type]),
    iconFn: (type) => (type === "all" ? undefined : dataTypeIcons[type]),
  }),
});

const updateAccountFields = (partial: Partial<AccountFieldsState>): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    accountFields: (accountFields) => accountFields && { ...accountFields, ...partial },
  },
});

const fetchDefinitions = (accountId: string, editDefinitionId?: string): DCEvent =>
  httpGet({
    url: `/enterprise-accounts/${accountId}/custom-fields`,
    onSuccess: (res: HttpResponse<CustomFieldDefinition[]>) => [
      updateAccountFields({ definitionsResult: ok(res.data) }),
      ...(editDefinitionId ? [editDefinitionById(res.data, editDefinitionId)] : []),
    ],
    onFailure: () => [updateAccountFields({ definitionsResult: failure() })],
  });

const editDefinitionById = (
  definitions: CustomFieldDefinition[],
  definitionId: string,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    accountFields: (accountFields) => {
      const definition = definitions.find((candidate) => candidate.id === definitionId);
      return accountFields && definition
        ? {
            ...accountFields,
            tab: definitionTab(definition),
            addFieldModel: new AddFieldFormModel(
              definition.dataType,
              definition,
              accountFields.companies,
            ),
          }
        : accountFields;
    },
  },
});

export const exportFieldsToExcel = (accountId: string): DCEvent => ({
  type: "download-file",
  url: `/enterprise-accounts/${accountId}/custom-fields/export`,
});

const refreshAfterImport = (state: AccountFieldsState): DCEvent[] => [
  fetchDefinitions(state.accountId),
  ...(state.fieldPanel
    ? [
        fetchDefinitionValues(state.accountId, state.fieldPanel.definitionId),
        fetchDefinitionLog(state.accountId, state.fieldPanel.definitionId),
      ]
    : []),
];

export const uploadFieldsExcel = (state: AccountFieldsState, files: File[] | null): DCEvent[] => {
  const file = files?.[0];
  if (!file) {
    return [];
  }
  const data = new FormData();
  data.append("file", file);
  return [
    updateAccountFields({ isImporting: true, importResult: undefined }),
    httpPost({
      url: `/enterprise-accounts/${state.accountId}/custom-fields/import-excel`,
      data,
      onSuccess: (res: HttpResponse<CustomFieldsImportResult>) => [
        updateAccountFields({ isImporting: false, importResult: res.data }),
        ...refreshAfterImport(state),
      ],
      onFailure: () => [
        updateAccountFields({ isImporting: false }),
        showError({ key: "entity-data.upload-error" }),
      ],
    }),
  ];
};

export const visibleDefinitions = (
  definitions: CustomFieldDefinition[],
  search: string,
  type: AccountFieldsTypeFilter,
  objectType: CustomFieldObjectType,
): CustomFieldDefinition[] => {
  const query = search.trim().toLowerCase();
  return definitions
    .filter((definition) => definition.objectType === objectType)
    .filter((definition) => type === "all" || definition.dataType === type)
    .filter((definition) => query === "" || definition.name.toLowerCase().includes(query));
};

const searchText = (item: CustomFieldSubjectItem): string =>
  (item.objectType === "COMPANY"
    ? item.companyName
    : `${item.name} ${item.companyName}`
  ).toLowerCase();

export const fieldValueRows = (
  definition: CustomFieldDefinition,
  subjects: CustomFieldSubjectItem[],
  values: SubjectFieldValue[],
  search: string,
  filter: FieldValueFilter,
): FieldValueRow[] => {
  const query = search.trim().toLowerCase();
  const entryBySubject = new Map(values.map((entry) => [entry.subject, entry]));
  return subjects
    .filter((item) => definitionAppliesTo(definition, item.orgNo))
    .map((item) => ({ ...entryBySubject.get(item.subject), ...item }))
    .filter((row) => query === "" || searchText(row).includes(query))
    .filter((row) => filter === "all" || (filter === "set") === (row.value !== undefined));
};

export type FieldLogRow = {
  id: string;
  label: TMessage;
  actorName: string;
  recordedAt: string;
  value?: CustomFieldValue;
};

const logSubjectName = (
  itemBySubject: Map<string, CustomFieldSubjectItem>,
  entry: CustomFieldEventEntry,
): string | undefined => {
  if (entry.orgNo === undefined) {
    return undefined;
  }
  const item = entry.subject === undefined ? undefined : itemBySubject.get(entry.subject);
  return item ? subjectDisplayName(item) : entry.orgNo;
};

export const fieldLogRows = (
  subjects: CustomFieldSubjectItem[],
  entries: CustomFieldEventEntry[],
): FieldLogRow[] => {
  const itemBySubject = new Map(subjects.map((item) => [item.subject, item]));
  return entries.map((entry) => ({
    id: entry.id,
    label: customFieldEventLabel(entry, logSubjectName(itemBySubject, entry)),
    actorName: entry.actor.name,
    recordedAt: entry.recordedAt,
    value: entry.payload.payloadType === "VALUE_SET" ? entry.payload.value : undefined,
  }));
};

const updateFieldPanel = (
  definitionId: string,
  partial: Partial<FieldPanelState>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    accountFields: (accountFields) =>
      accountFields &&
      (accountFields.fieldPanel && accountFields.fieldPanel.definitionId === definitionId
        ? { ...accountFields, fieldPanel: { ...accountFields.fieldPanel, ...partial } }
        : accountFields),
  },
});

const fetchDefinitionValues = (accountId: string, definitionId: string): DCEvent =>
  httpGet({
    url: `/enterprise-accounts/${accountId}/custom-fields/${definitionId}/values`,
    onSuccess: (res: HttpResponse<SubjectFieldValue[]>) => [
      updateFieldPanel(definitionId, { valuesResult: ok(res.data) }),
    ],
    onFailure: () => [updateFieldPanel(definitionId, { valuesResult: failure() })],
  });

const fetchDefinitionLog = (accountId: string, definitionId: string): DCEvent =>
  httpGet({
    url: `/enterprise-accounts/${accountId}/custom-fields/${definitionId}/history`,
    onSuccess: (res: HttpResponse<CustomFieldEventEntry[]>) => [
      updateFieldPanel(definitionId, { logResult: ok(res.data) }),
    ],
    onFailure: () => [updateFieldPanel(definitionId, { logResult: failure() })],
  });

export const defaultFieldPanelState = (
  definitionId: string,
  objectType: CustomFieldObjectType = "COMPANY",
): FieldPanelState => ({
  definitionId,
  tab: "values",
  valuesResult: pending(),
  logResult: pending(),
  valueSearchModel: new StringFieldModel(),
  valueFilter: new RadioFieldModel<FieldValueFilter>({
    value: "all",
    options: ["all", "set", "not-set"],
    idFn: (filter) => filter,
    labelFn: (filter) =>
      filter === "all"
        ? { key: objectType === "COMPANY" ? "all-companies" : "all" }
        : filter === "set"
          ? { key: "entity-data.value-set" }
          : { key: "entity-data.not-set" },
  }),
});

const openFieldPanel = (
  state: AccountFieldsState,
  definition: CustomFieldDefinition,
): DCEvent[] => [
  updateAccountFields({ fieldPanel: defaultFieldPanelState(definition.id, definition.objectType) }),
  fetchDefinitionValues(state.accountId, definition.id),
  fetchDefinitionLog(state.accountId, definition.id),
];

const closeFieldPanel = (): DCEvent => updateAccountFields({ fieldPanel: undefined });

const openPanelEditValue = (
  definition: CustomFieldDefinition,
  row: FieldValueRow,
  language: Language,
): DCEvent[] => {
  const model = new EntityValueFormModel(toCompanyCustomField(definition, row), language);
  return [
    updateFieldPanel(definition.id, { editValue: { row, model } }),
    ...fetchContactCandidates(row.orgNo, model),
  ];
};

const submitPanelEditValue = (state: AccountFieldsState): DCEvent[] => {
  const panel = state.fieldPanel;
  const editValue = panel?.editValue;
  if (!panel || !editValue || !editValue.model.validate()) {
    return [];
  }
  editValue.model.setSubmitting(true);
  return [
    httpPost({
      url: `/custom-fields/${editValue.row.orgNo}/commands`,
      data: setValueCommand(panel.definitionId, editValue.row.subject, editValue.model.toInput()),
      onSuccess: () => [
        updateFieldPanel(panel.definitionId, { editValue: undefined }),
        fetchDefinitionValues(state.accountId, panel.definitionId),
        fetchDefinitionLog(state.accountId, panel.definitionId),
      ],
      onFailure: (err) => {
        editValue.model.setSubmitting(false);
        editValue.model.setError(commandError(err));
        return [];
      },
    }),
  ];
};

const openPanelValueHistory = (
  definition: CustomFieldDefinition,
  row: FieldValueRow,
): DCEvent[] => {
  const field = toCompanyCustomField(definition, row);
  const subjectName = subjectDisplayName(row);
  return [
    updateFieldPanel(definition.id, { history: { subjectName, field, result: pending() } }),
    httpGet({
      url: `/custom-fields/${row.orgNo}/subjects/${row.subject}/${definition.id}/history`,
      onSuccess: (res: HttpResponse<CustomFieldEventEntry[]>) => [
        updateFieldPanel(definition.id, { history: { subjectName, field, result: ok(res.data) } }),
      ],
      onFailure: () => [
        updateFieldPanel(definition.id, { history: { subjectName, field, result: failure() } }),
      ],
    }),
  ];
};

const openAddField = (
  dataType: CustomFieldDataType,
  companies: AccountCompanyOption[],
  objectType: CustomFieldObjectType,
): DCEvent =>
  updateAccountFields({
    addFieldModel: new AddFieldFormModel(dataType, undefined, companies, objectType),
  });

const openEditField = (
  definition: CustomFieldDefinition,
  companies: AccountCompanyOption[],
): DCEvent =>
  updateAccountFields({
    addFieldModel: new AddFieldFormModel(definition.dataType, definition, companies),
  });

const closeAddField = (): DCEvent => updateAccountFields({ addFieldModel: undefined });

const updateDeleteField = (
  definitionId: string,
  partial: Partial<DeleteFieldState>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    accountFields: (accountFields) =>
      accountFields &&
      (accountFields.deleteField && accountFields.deleteField.definition.id === definitionId
        ? { ...accountFields, deleteField: { ...accountFields.deleteField, ...partial } }
        : accountFields),
  },
});

const openDeleteField = (
  state: AccountFieldsState,
  definition: CustomFieldDefinition,
): DCEvent[] => [
  updateAccountFields({
    deleteField: { definition, valuesResult: pending(), isDeleting: false },
  }),
  httpGet({
    url: `/enterprise-accounts/${state.accountId}/custom-fields/${definition.id}/values`,
    onSuccess: (res: HttpResponse<SubjectFieldValue[]>) => [
      updateDeleteField(definition.id, { valuesResult: ok(res.data) }),
    ],
    onFailure: () => [updateDeleteField(definition.id, { valuesResult: failure() })],
  }),
];

const closeDeleteField = (): DCEvent => updateAccountFields({ deleteField: undefined });

const submitDeleteField = (state: AccountFieldsState): DCEvent[] => {
  const deleteField = state.deleteField;
  if (!deleteField) {
    return [];
  }
  const definitionId = deleteField.definition.id;
  return [
    updateDeleteField(definitionId, { isDeleting: true, error: undefined }),
    httpPost({
      url: `/enterprise-accounts/${state.accountId}/custom-fields/commands`,
      data: { commandType: "DELETE_FIELD", definitionId },
      onSuccess: () => [
        updateAccountFields({
          deleteField: undefined,
          addFieldModel: undefined,
          fieldPanel:
            state.fieldPanel?.definitionId === definitionId ? undefined : state.fieldPanel,
        }),
        fetchDefinitions(state.accountId),
      ],
      onFailure: (err) => [
        updateDeleteField(definitionId, { isDeleting: false, error: commandError(err) }),
      ],
    }),
  ];
};

export const deleteFieldRows = (
  subjects: CustomFieldSubjectItem[],
  values: SubjectFieldValue[],
): DeleteFieldRow[] => {
  const itemBySubject = new Map(subjects.map((item) => [item.subject, item]));
  return values
    .map((entry) => {
      const item = itemBySubject.get(entry.subject);
      return { ...entry, label: item ? subjectDisplayName(item) : entry.orgNo };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

const combineResults = <A, B>(a: Result<A>, b: Result<B>): Result<[A, B]> =>
  isOk(a) && isOk(b) ? ok([a.data, b.data]) : isFailure(a) || isFailure(b) ? failure() : pending();

const submitAddField = (state: AccountFieldsState): DCEvent[] => {
  const model = state.addFieldModel;
  if (!model || !model.validate()) {
    return [];
  }
  model.setSubmitting(true);
  return [
    httpPost({
      url: `/enterprise-accounts/${state.accountId}/custom-fields/commands`,
      data: model.toCommand(),
      onSuccess: () => [
        closeAddField(),
        fetchDefinitions(state.accountId),
        ...(state.fieldPanel
          ? [
              fetchDefinitionValues(state.accountId, state.fieldPanel.definitionId),
              fetchDefinitionLog(state.accountId, state.fieldPanel.definitionId),
            ]
          : []),
      ],
      onFailure: (err) => {
        model.setSubmitting(false);
        model.setError(commandError(err));
        return [];
      },
    }),
  ];
};

export type FieldPanelViewProps = {
  definition: CustomFieldDefinition;
  tab: FieldPanelTab;
  valuesResult: Result<FieldValueRow[]>;
  valueRows: FieldValueRow[];
  valueSearchModel: StringFieldModel;
  valueFilter: RadioFieldModel<FieldValueFilter>;
  logResult: Result<CustomFieldEventEntry[]>;
  logRows: FieldLogRow[];
  editValue?: PanelValueEdit;
  history?: PanelValueHistory;
  onSelectTab: (tab: FieldPanelTab) => void;
  onUpdateValue: (row: FieldValueRow) => void;
  onCloseEditValue: () => void;
  onSubmitEditValue: () => void;
  onShowValueHistory: (row: FieldValueRow) => void;
  onCloseValueHistory: () => void;
};

export type DeleteFieldViewProps = {
  definition: CustomFieldDefinition;
  valuesResult: Result<SubjectFieldValue[]>;
  rows: DeleteFieldRow[];
  isDeleting: boolean;
  error?: TMessage;
  onConfirm: () => void;
  onCancel: () => void;
};

export type AccountFieldsViewProps = {
  accountName: string;
  tab: AccountFieldsTab;
  definitionsResult: Result<CustomFieldDefinition[]>;
  definitions: CustomFieldDefinition[];
  searchModel: StringFieldModel;
  typeFilter: RadioFieldModel<AccountFieldsTypeFilter>;
  addFieldModel?: AddFieldFormModel;
  deleteField?: DeleteFieldViewProps;
  fieldPanel?: FieldPanelViewProps;
  isImporting: boolean;
  importResult?: CustomFieldsImportResult;
  uploadConfig?: FileUploadConfig;
  onSelectTab: (tab: AccountFieldsTab) => void;
  onAddField: (dataType: CustomFieldDataType) => void;
  onExportFields: () => void;
  onUploadFields: (files: File[] | null) => void;
  onImportEntityData: (files: File[] | null) => void;
  onImportCompanyRoles: (files: File[] | null) => void;
  onImportOwnership: (files: File[] | null) => void;
  onImportFieldHistory: (files: File[] | null) => void;
  onCloseImportResult: () => void;
  onEditField: (definition: CustomFieldDefinition) => void;
  onCloseAddField: () => void;
  onSubmitAddField: () => void;
  onDeleteField: () => void;
  onSelectField: (definition: CustomFieldDefinition) => void;
  onCloseFieldPanel: () => void;
};

const prepDeleteField = (
  state: AccountFieldsState,
  dispatch: EventDispatcher,
): DeleteFieldViewProps | undefined => {
  const deleteField = state.deleteField;
  return (
    deleteField && {
      definition: deleteField.definition,
      valuesResult: deleteField.valuesResult,
      rows: isOk(deleteField.valuesResult)
        ? deleteFieldRows(
            subjectsOf(state, deleteField.definition.objectType),
            deleteField.valuesResult.data,
          )
        : [],
      isDeleting: deleteField.isDeleting,
      error: deleteField.error,
      onConfirm: () => dispatch(submitDeleteField(state)),
      onCancel: () => dispatch([closeDeleteField()]),
    }
  );
};

const prepFieldPanel = (
  state: AccountFieldsState,
  dispatch: EventDispatcher,
  lang: Language,
): FieldPanelViewProps | undefined => {
  const panel = state.fieldPanel;
  if (!panel || !isOk(state.definitionsResult)) {
    return undefined;
  }
  const definition = state.definitionsResult.data.find((d) => d.id === panel.definitionId);
  if (!definition) {
    return undefined;
  }
  const subjects = subjectsOf(state, definition.objectType);
  const loaded = combineResults(state.subjects[definition.objectType], panel.valuesResult);
  const rows = (search: string, filter: FieldValueFilter) =>
    isOk(loaded) ? fieldValueRows(definition, loaded.data[0], loaded.data[1], search, filter) : [];
  return {
    definition,
    tab: panel.tab,
    valuesResult: isOk(loaded) ? ok(rows("", "all")) : loaded,
    valueRows: rows(panel.valueSearchModel.inputValue, panel.valueFilter.value),
    valueSearchModel: panel.valueSearchModel,
    valueFilter: panel.valueFilter,
    logResult: panel.logResult,
    logRows: isOk(panel.logResult) ? fieldLogRows(subjects, panel.logResult.data) : [],
    editValue: panel.editValue,
    history: panel.history,
    onSelectTab: (tab) => dispatch([updateFieldPanel(panel.definitionId, { tab })]),
    onUpdateValue: (row) => dispatch(openPanelEditValue(definition, row, lang)),
    onCloseEditValue: () =>
      dispatch([updateFieldPanel(panel.definitionId, { editValue: undefined })]),
    onSubmitEditValue: () => dispatch(submitPanelEditValue(state)),
    onShowValueHistory: (row) => dispatch(openPanelValueHistory(definition, row)),
    onCloseValueHistory: () =>
      dispatch([updateFieldPanel(panel.definitionId, { history: undefined })]),
  };
};

export const prepAccountFieldsView = ({
  state,
  dispatch,
  lang,
}: {
  state: AccountFieldsState | undefined;
  dispatch: EventDispatcher;
  lang: Language;
}): AccountFieldsViewProps | undefined =>
  state && {
    accountName: state.accountName,
    tab: state.tab,
    definitionsResult: state.definitionsResult,
    definitions: isOk(state.definitionsResult)
      ? visibleDefinitions(
          state.definitionsResult.data,
          state.searchModel.inputValue,
          state.typeFilter.value,
          tabObjectType[state.tab],
        )
      : [],
    searchModel: state.searchModel,
    typeFilter: state.typeFilter,
    addFieldModel: state.addFieldModel,
    deleteField: prepDeleteField(state, dispatch),
    fieldPanel: prepFieldPanel(state, dispatch, lang),
    isImporting: state.isImporting,
    importResult: state.importResult,
    uploadConfig: state.uploadConfig,
    onSelectTab: (tab) => dispatch([updateAccountFields({ tab, fieldPanel: undefined })]),
    onAddField: (dataType) =>
      dispatch([openAddField(dataType, state.companies, tabObjectType[state.tab])]),
    onExportFields: () => dispatch([exportFieldsToExcel(state.accountId)]),
    onUploadFields: (files) => dispatch(uploadFieldsExcel(state, files)),
    onImportEntityData: (files) =>
      dispatch(openEntityDataImport(state.accountId, files, refreshAfterImport(state))),
    onImportCompanyRoles: (files) =>
      dispatch(openCompanyRoleImport(state.accountId, files, refreshAfterImport(state))),
    onImportOwnership: (files) =>
      dispatch(openOwnershipImport(state.accountId, files, refreshAfterImport(state))),
    onImportFieldHistory: (files) =>
      dispatch(openFieldHistoryImport(state.accountId, files, refreshAfterImport(state))),
    onCloseImportResult: () => dispatch([updateAccountFields({ importResult: undefined })]),
    onEditField: (definition) => dispatch([openEditField(definition, state.companies)]),
    onCloseAddField: () => dispatch([closeAddField()]),
    onSubmitAddField: () => dispatch(submitAddField(state)),
    onDeleteField: () => {
      const definition = state.addFieldModel?.definition;
      if (definition) {
        dispatch(openDeleteField(state, definition));
      }
    },
    onSelectField: (definition) => dispatch(openFieldPanel(state, definition)),
    onCloseFieldPanel: () => dispatch([closeFieldPanel()]),
  };
